import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  applyPortalAuth,
  ensurePortalSession,
  setPortalSessionHeaderName,
  type PortalAuthMeResponse,
  type PortalPublicConfig,
  type PortalUserProfile,
} from '../services/api';
import {
  initTelegramWebApp,
  isTelegramWebApp,
  loadTelegramWebAppScript,
  looksLikeTelegramEnvironment,
  resolveTelegramInitData,
  setCachedTelegramInitData,
} from '../utils/telegramWebApp';
import { captureWebsiteAccountFromAuth } from '../utils/websiteCredentials';

interface AuthContextValue {
  user: PortalUserProfile | null;
  loading: boolean;
  config: PortalPublicConfig | null;
  authError: string | null;
  isAdmin: boolean;
  login: (payload: Record<string, unknown>) => Promise<boolean>;
  loginWithPassword: (username: string, password: string) => Promise<{ ok: boolean; message?: string }>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function applyAuth(data: PortalAuthMeResponse | undefined): {
  user: PortalUserProfile | null;
  isAdmin: boolean;
} {
  applyPortalAuth(data);
  captureWebsiteAccountFromAuth(data);
  if (data?.authenticated && data.user) {
    return { user: data.user, isAdmin: Boolean(data.is_admin) };
  }
  return { user: null, isAdmin: false };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PortalUserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [config, setConfig] = useState<PortalPublicConfig | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const res = await api.getMe();
    let next = applyAuth(res.data);
    if (!next.user) {
      if (await ensurePortalSession()) {
        const retry = await api.getMe();
        next = applyAuth(retry.data);
      }
    }
    setUser(next.user);
    setIsAdmin(next.isAdmin);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (looksLikeTelegramEnvironment()) {
          await loadTelegramWebAppScript();
        }
        const inTelegram = isTelegramWebApp();
        if (inTelegram) {
          initTelegramWebApp();
        }

        const cfgRes = await api.getConfig();
        if (cfgRes.data) {
          setConfig(cfgRes.data);
          setPortalSessionHeaderName(cfgRes.data.session_header_name);
        }

        let next = { user: null as PortalUserProfile | null, isAdmin: false };

        if (inTelegram) {
          const initData = await resolveTelegramInitData();
          if (!initData) {
            setAuthError('Telegram не передал данные для входа');
          } else {
            setCachedTelegramInitData(initData);
            const webAppLogin = await api.loginTelegramWebApp(initData);
            next = applyAuth(webAppLogin.data);
            if (!next.user) {
              setAuthError(webAppLogin.error?.message ?? 'Не удалось войти через Telegram');
            }
          }
        }

        if (!next.user) {
          const me = await api.getMe();
          next = applyAuth(me.data);
        }

        setUser(next.user);
        setIsAdmin(next.isAdmin);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback(async (payload: Record<string, unknown>) => {
    const res = await api.loginTelegram(payload);
    if (res.success && res.data?.authenticated) {
      const next = applyAuth(res.data);
      setUser(next.user);
      setIsAdmin(next.isAdmin);
      return true;
    }
    return false;
  }, []);

  const loginWithPassword = useCallback(async (username: string, password: string) => {
    // Drop stale session before bootstrap login.
    applyPortalAuth(undefined);
    const res = await api.loginPassword(username.trim().replace(/^@/, ''), password);
    if (res.success && res.data?.authenticated) {
      const next = applyAuth(res.data);
      setUser(next.user);
      setIsAdmin(next.isAdmin);
      setAuthError(null);
      return { ok: true };
    }
    return {
      ok: false,
      message: res.error?.message ?? 'Неверный логин или пароль',
    };
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setUser(null);
    setIsAdmin(false);
    applyPortalAuth(undefined);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      config,
      authError,
      isAdmin,
      login,
      loginWithPassword,
      logout,
      refresh,
    }),
    [user, loading, config, authError, isAdmin, login, loginWithPassword, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
